// AGC: DroidFarm - Main App Component
// AGC: HashRouter-based routing, authentication, and protected routes
import React, { useState, useEffect, useCallback, Suspense, lazy, Component, type ErrorInfo, type ReactNode } from 'react'
import { HashRouter, Routes, Route, useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from './hooks/useTranslation'
import { Capacitor } from '@capacitor/core'
import { App as CapApp } from '@capacitor/app'
import { playFeedback } from './services/feedbackService'
import { LotesProvider, AuthProvider, useAuthContext, VoiceProvider, SubscriptionProvider, FarmProvider, useFarms, DeleteConfirmProvider, FinancialProvider } from './context'
import { AdminLayout } from './components/admin'
import { UpgradeModal } from './components/subscription'
import { FarmSetupWizard, WelcomePage } from './components/wizard'
import { LotCreationWizard } from './components/wizard/LotCreationWizard'
import type { Field } from './types'
import { MainLayout } from './components/common/MainLayout'
import { LegacyMainMenuAdapter } from './components/common/LegacyMainMenuAdapter'

// AGC: Lazy-loaded screens for code splitting
const DashboardScreen = lazy(() => import('./screens/DashboardScreen'))
const ProfileScreen = lazy(() => import('./screens/ProfileScreen'))
const NotificationsScreen = lazy(() => import('./screens/NotificationsScreen'))
const LotesListScreen = lazy(() => import('./screens/LotesListScreen'))
const LotPageScreen = lazy(() => import('./screens/LotPageScreen'))
const NovoLoteScreen = lazy(() => import('./screens/NovoLoteScreen'))
const CreateWithGPSScreen = lazy(() => import('./screens/CreateWithGPSScreen'))
const AIOneClickScreen = lazy(() => import('./screens/AIOneClickScreen'))
const AutoDetectScreen = lazy(() => import('./screens/AutoDetectScreen'))
const FinalizarLoteScreen = lazy(() => import('./screens/FinalizarLoteScreen'))
const PolygonMapScreen = lazy(() => import('./screens/PolygonMapScreen'))
const FarmLotsMapScreen = lazy(() => import('./screens/FarmLotsMapScreen'))
const CropSuggestionsScreen = lazy(() => import('./screens/CropSuggestionsScreen'))
const FieldTaskPlanningScreen = lazy(() => import('./screens/FieldTaskPlanningScreen'))
const TasksListScreen = lazy(() => import('./screens/TasksListScreen'))
const NewTaskScreen = lazy(() => import('./screens/NewTaskScreen'))
const TaskDetailScreen = lazy(() => import('./screens/TaskDetailScreen'))
const WeatherScreen = lazy(() => import('./screens/WeatherScreen'))
const SpraySuitabilityScreen = lazy(() => import('./screens/SpraySuitabilityScreen'))
const ForecastScreen = lazy(() => import('./screens/ForecastScreen'))
const FinancesScreen = lazy(() => import('./screens/FinancesScreen'))
const ExpensesScreen = lazy(() => import('./screens/ExpensesScreen'))
const NewExpenseScreen = lazy(() => import('./screens/NewExpenseScreen'))
const NewRevenueScreen = lazy(() => import('./screens/NewRevenueScreen'))
const SettingsScreen = lazy(() => import('./screens/SettingsScreen'))
const SubscriptionScreen = lazy(() => import('./screens/SubscriptionScreen'))
const InventoryScreen = lazy(() => import('./screens/InventoryScreen'))
const PurchaseOrdersScreen = lazy(() => import('./screens/PurchaseOrdersScreen'))
const MachineryScreen = lazy(() => import('./screens/MachineryScreen'))
const MachineDetailScreen = lazy(() => import('./screens/MachineDetailScreen'))
const TeamScreen = lazy(() => import('./screens/TeamScreen'))
const SorteiosScreen = lazy(() => import('./screens/SorteiosScreen'))
const InviteAcceptScreen = lazy(() => import('./screens/InviteAcceptScreen'))
import {
  LoginPage,
  RegisterPage,
  BiometricLockScreen,
  BiometricPromptModal,
  AdminLogin,
  LoadingScreen,
} from './components/auth'
import {
  isBiometricEnabled,
  shouldShowBiometricPrompt,
  clearBiometricPreferences,
} from './services/biometricPreferences'
import { RequestPermissionsOnMount } from './components/common/RequestPermissionsOnMount'
import { PermissionBlockedModal } from './components/common/PermissionBlockedModal'
// AGC: useIsDesktopLayout now used inside MainLayout, not here

// AGC: Module-level flag — survives component remounts (e.g. auth state flickers)
let hasUnlockedThisSessionFlag = false

/**
 * AGC: AppErrorBoundary — catches unhandled render errors so the app shows
 * AGC: a friendly recovery screen instead of a white-screen crash.
 * AGC: Critical for offline startup where providers may throw.
 */
interface ErrorBoundaryState { hasError: boolean; error: Error | null }

class AppErrorBoundary extends Component<{ children: ReactNode }, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false, error: null }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // AGC: Log to structured logger in production; console.error OK in dev boundary
    console.error('AGC: AppErrorBoundary caught:', error, info.componentStack)
  }

  render() {
    if (this.state.hasError) {
      const isOffline = !navigator.onLine
      return (
        <div className="min-h-screen flex items-center justify-center bg-[#F4F5F7] dark:bg-slate-900 p-6">
          <div className="text-center max-w-sm">
            <div className="text-5xl mb-4">{isOffline ? '📡' : '⚠️'}</div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
              {isOffline ? 'Sem conexão' : 'Algo deu errado'}
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
              {isOffline
                ? 'Conecte-se à internet e tente novamente. Seus dados locais estão salvos.'
                : 'Ocorreu um erro inesperado. Tente reiniciar o aplicativo.'}
            </p>
            <button
              onClick={() => {
                this.setState({ hasError: false, error: null })
                window.location.reload()
              }}
              className="px-6 py-3 bg-[#46b179] text-white rounded-xl font-medium text-sm shadow-lg"
            >
              {isOffline ? 'Tentar novamente' : 'Reiniciar'}
            </button>
          </div>
        </div>
      )
    }
    return this.props.children
  }
}

/**
 * AGC: PageLoader — full-screen loading fallback for Suspense
 */
const PageLoader: React.FC = () => (
  <div className="min-h-screen flex items-center justify-center bg-[#F4F5F7] dark:bg-slate-900">
    <div className="text-center">
      <div className="animate-spin rounded-full h-10 w-10 border-2 border-[#46b179]/30 border-t-[#46b179] mx-auto mb-3" />
    </div>
  </div>
)

/**
 * AGC: AdminRoute — Handles admin panel auth + rendering
 * AGC: Separated from main app flow for clean React Router routing
 */
const AdminRoute: React.FC = () => {
  const { t } = useTranslation()
  const { user, isLoading, isAuthenticated } = useAuthContext()

  if (isLoading) {
    return <LoadingScreen message={t('common.checkingAuth')} />
  }

  if (!isAuthenticated || user?.profile?.role !== 'admin') {
    return <AdminLogin onLoginSuccess={() => {}} />
  }

  // AGC: Admin is authenticated, show admin panel
  // AGC: VoiceProvider needed for VoiceConfigAdmin component
  // AGC: SubscriptionProvider required for VoiceProvider to access subscription tier
  return (
    <SubscriptionProvider>
      <VoiceProvider>
        <AdminLayout />
      </VoiceProvider>
    </SubscriptionProvider>
  )
}

/**
 * AGC: AuthenticatedApp - Main app content (requires authentication)
 * AGC: Handles auth state, biometric lock, and renders main app flow
 */
const AuthenticatedApp: React.FC = () => {
  const { t } = useTranslation()
  const {
    isLoading,
    isAuthenticated,
    logout,
  } = useAuthContext()
  const location = useLocation()
  const navigate = useNavigate()

  // AGC: Auth page state (used only on native)
  const [authPage, setAuthPage] = useState<'login' | 'register'>('login')

  // AGC: React state for biometric unlock — triggers re-render when set to true
  // AGC: Module-level flag is kept as backup for component remount persistence
  const [hasUnlockedThisSession, setHasUnlockedThisSession] = useState(hasUnlockedThisSessionFlag)

  // AGC: Push notification permission state (must be declared before conditional returns)
  const [pushBlocked, setPushBlocked] = useState(false)
  const [pushToast, setPushToast] = useState<{ title: string; body: string } | null>(null)

  const handlePushBlocked = useCallback(() => setPushBlocked(true), [])
  const handlePushNotification = useCallback((title: string, body: string) => {
    setPushToast({ title, body })
    setTimeout(() => setPushToast(null), 5000)
  }, [])

  // AGC: Show loading screen while checking auth state
  if (isLoading) {
    return <LoadingScreen message={t('common.checkingAuth')} />
  }

  // AGC: Not authenticated — show landing page (web) or login (native)
  if (!isAuthenticated) {
    const isNative = Capacitor.isNativePlatform()

    // AGC: Explicit /login route — show login page
    if (location.pathname === '/login') {
      return <LoginPage onNavigateToRegister={() => navigate('/register')} />
    }

    // AGC: Explicit /register route — show register page
    if (location.pathname === '/register') {
      return <RegisterPage onNavigateToLogin={() => navigate('/login')} />
    }

    // AGC: Native (Capacitor), app.droidfarm.com, or local dev — always show inline login.
    const isAppSubdomain = window.location.hostname === 'app.droidfarm.com'
    // AGC: [DEV] On a Vite dev build or localhost/127.0.0.1 we must NOT bounce to the production
    // AGC: marketing site on logout — that breaks local testing. import.meta.env.DEV is false in
    // AGC: production builds and the hostname checks only match local, so production is unaffected.
    const isLocalDev = import.meta.env.DEV
      || window.location.hostname === 'localhost'
      || window.location.hostname === '127.0.0.1'
    if (isNative || isAppSubdomain || isLocalDev) {
      if (authPage === 'register') {
        return <RegisterPage onNavigateToLogin={() => setAuthPage('login')} />
      }
      return <LoginPage onNavigateToRegister={() => setAuthPage('register')} />
    }

    // AGC: Web (droidfarm-app.web.app or other) — redirect to landing page
    window.location.href = 'https://www.droidfarm.com/'
    return <LoadingScreen message={t('common.checkingAuth')} />
  }

  // AGC: Biometric lock screen - fresh app open only (Yitix-style)
  // AGC: Not shown on app resume from background
  if (
    Capacitor.isNativePlatform() &&
    isBiometricEnabled() &&
    !hasUnlockedThisSession
  ) {
    return (
      <BiometricLockScreen
        onUnlock={() => {
          hasUnlockedThisSessionFlag = true
          setHasUnlockedThisSession(true)
        }}
        onManualLogin={async () => {
          clearBiometricPreferences()
          hasUnlockedThisSessionFlag = true
          setHasUnlockedThisSession(true)
          await logout()
        }}
      />
    )
  }

  // AGC: Authenticated - show main app
  // AGC: Request permissions early (GPS, push, etc.) - runs for both FarmSetupWizard and MainMenu
  return (
    <LotesProvider>
      <FarmProvider>
        <RequestPermissionsOnMount
          onPushBlocked={handlePushBlocked}
          onPushNotification={handlePushNotification}
        />
        <MainAppWithBiometricPrompt />

        {/* AGC: Push notification permission blocked modal */}
        <PermissionBlockedModal
          isOpen={pushBlocked}
          permissionType="notifications"
          onClose={() => setPushBlocked(false)}
        />

        {/* AGC: Foreground push notification toast */}
        {pushToast && (
          <div
            className="fixed top-4 left-4 right-4 z-[9999] bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 p-4 flex items-start gap-3 animate-slide-down"
            onClick={() => setPushToast(null)}
          >
            <div className="w-10 h-10 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center flex-shrink-0">
              <span className="text-lg">🔔</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                {pushToast.title}
              </p>
              <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5 line-clamp-2">
                {pushToast.body}
              </p>
            </div>
          </div>
        )}
      </FarmProvider>
    </LotesProvider>
  )
}

/**
 * AGC: MainAppWithBiometricPrompt - Wraps main app with Yitix-style biometric prompt
 * AGC: Shows "Enable Biometric?" modal overlay when shouldShowBiometricPrompt()
 */
const MainAppWithBiometricPrompt: React.FC = () => {
  const [showPrompt, setShowPrompt] = useState(() => shouldShowBiometricPrompt())

  return (
    <>
      <MainAppWithFarmCheck />
      {/* AGC: Yitix-style biometric prompt (Enable / Not Now / Don't Ask Again) */}
      {showPrompt && (
        <BiometricPromptModal
          onClose={() => setShowPrompt(false)}
        />
      )}
    </>
  )
}

/**
 * AGC: MainAppWithFarmCheck - Checks if user needs farm wizard
 * AGC: Shows wizard for first-time users with no farms
 */
const MainAppWithFarmCheck: React.FC = () => {
  const { t } = useTranslation()
  const { needsWizard, isLoading, addFarm, dismissWizard } = useFarms()
  const [showWelcome, setShowWelcome] = useState(false)
  const [lotWizardFarm, setLotWizardFarm] = useState<Field | null>(null)

  // AGC: Show loading while checking farms
  if (isLoading) {
    return <LoadingScreen message={t('common.loadingFarms')} />
  }

  // AGC: Show wizard if user has no farms
  if (needsWizard) {
    return (
      <FarmSetupWizard
        onComplete={(farm) => addFarm(farm)}
        onSkip={dismissWizard}
        onStartLotWizard={(farm) => setLotWizardFarm(farm)}
      />
    )
  }

  // AGC: Show lot creation wizard after farm setup
  if (lotWizardFarm) {
    return (
      <LotCreationWizard
        farm={lotWizardFarm}
        onComplete={() => {
          setLotWizardFarm(null)
          setShowWelcome(true)
        }}
        onSkip={() => {
          setLotWizardFarm(null)
          setShowWelcome(true)
        }}
      />
    )
  }

  // AGC: Show welcome page after wizard completion (one-time)
  if (showWelcome) {
    return <WelcomePage onContinue={() => setShowWelcome(false)} onGoToPlans={() => { setShowWelcome(false); window.location.hash = '#/subscription' }} />
  }

  // AGC: Show main app with all providers + route tree via MainLayout
  return (
    <SubscriptionProvider>
      <VoiceProvider>
        <DeleteConfirmProvider>
          <FinancialProvider>
            <Routes>
              <Route element={<MainLayout />}>
                {/* AGC: Dashboard */}
                <Route index element={<DashboardScreen />} />

                {/* AGC: Profile / Notifications / Subscription */}
                <Route path="profile" element={<ProfileScreen />} />
                <Route path="notifications" element={<NotificationsScreen />} />
                <Route path="subscription" element={<SubscriptionScreen />} />

                {/* AGC: Lotes / Fields */}
                <Route path="lotes" element={<LotesListScreen />} />
                <Route path="lotes/new" element={<NovoLoteScreen />} />
                <Route path="lotes/new/gps" element={<CreateWithGPSScreen />} />
                <Route path="lotes/new/ai" element={<AIOneClickScreen />} />
                <Route path="lotes/new/auto-detect" element={<AutoDetectScreen />} />
                <Route path="lotes/finalize" element={<FinalizarLoteScreen />} />
                <Route path="lotes/polygon" element={<PolygonMapScreen />} />
                <Route path="lotes/map" element={<FarmLotsMapScreen />} />
                <Route path="lotes/crop-suggestions" element={<CropSuggestionsScreen />} />
                <Route path="lotes/task-planning" element={<FieldTaskPlanningScreen />} />
                <Route path="lotes/view" element={<LotPageScreen />} />

                {/* AGC: Tasks */}
                <Route path="tasks" element={<TasksListScreen />} />
                <Route path="tasks/new" element={<NewTaskScreen />} />
                <Route path="tasks/view" element={<TaskDetailScreen />} />

                {/* AGC: Weather */}
                <Route path="weather" element={<WeatherScreen />} />
                <Route path="weather/spray" element={<SpraySuitabilityScreen />} />
                <Route path="weather/forecast" element={<ForecastScreen />} />

                {/* AGC: Financial */}
                <Route path="finances" element={<FinancesScreen />} />
                <Route path="expenses" element={<ExpensesScreen />} />
                <Route path="expenses/new" element={<NewExpenseScreen />} />
                <Route path="revenues/new" element={<NewRevenueScreen />} />

                {/* AGC: Settings */}
                <Route path="settings" element={<SettingsScreen />} />

                {/* AGC: Inventory / Machinery / Team */}
                <Route path="inventory" element={<InventoryScreen />} />
                <Route path="pedidos" element={<PurchaseOrdersScreen />} />
                <Route path="machinery" element={<MachineryScreen />} />
                <Route path="machinery/view" element={<MachineDetailScreen />} />
                <Route path="team" element={<TeamScreen />} />

                {/* AGC: Sorteios — user-facing raffle page under Equipe */}
                <Route path="sorteios" element={<SorteiosScreen />} />

                {/* AGC: Catch-all — un-migrated screens go through legacy MainMenu adapter */}
                <Route path="*" element={<LegacyMainMenuAdapter />} />
              </Route>
            </Routes>
            {/* AGC: Global upgrade modal for limit prompts */}
            <UpgradeModal />
          </FinancialProvider>
        </DeleteConfirmProvider>
      </VoiceProvider>
    </SubscriptionProvider>
  )
}

/**
 * AGC: BackButtonHandler - Intercepts Android hardware back button via @capacitor/app.
 * AGC: On sub-pages: navigates back. On root (/): shows exit confirmation dialog.
 * AGC: Must be rendered inside HashRouter for useLocation/useNavigate access.
 */
const BackButtonHandler: React.FC = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [showExitDialog, setShowExitDialog] = useState(false)

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return

    const listener = CapApp.addListener('backButton', ({ canGoBack }) => {
      // AGC: If on the root/dashboard, show exit confirmation
      if (location.pathname === '/' || location.pathname === '') {
        setShowExitDialog(true)
      } else if (canGoBack) {
        // AGC: Navigate back within the app
        navigate(-1)
      } else {
        setShowExitDialog(true)
      }
    })

    return () => {
      listener.then(h => h.remove())
    }
  }, [location.pathname, navigate])

  if (!showExitDialog) return null

  return (
    <div className="fixed inset-0 z-[9999] bg-black/50 flex items-center justify-center p-6">
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden">
        <div className="p-6 text-center">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            {t('common.exitApp') || 'Sair do aplicativo?'}
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t('common.exitAppMessage') || 'Deseja sair do Droidfarm?'}
          </p>
        </div>
        <div className="flex border-t border-gray-200 dark:border-slate-700">
          <button
            onClick={() => setShowExitDialog(false)}
            className="flex-1 py-3.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
          >
            {t('common.stay') || 'Ficar'}
          </button>
          <button
            onClick={() => CapApp.exitApp()}
            className="flex-1 py-3.5 text-sm font-medium text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors border-l border-gray-200 dark:border-slate-700"
          >
            {t('common.exit') || 'Sair'}
          </button>
        </div>
      </div>
    </div>
  )
}

/**
 * AGC: Main App Component
 * AGC: Wraps everything in AuthProvider + HashRouter for route-based navigation
 * AGC: Phase 2+ will add routes inside MainLayout; for now MainMenu handles all screens
 */
const App: React.FC = () => {
  // AGC: Global click delegation - plays feedback for button/role=button clicks
  // AGC: Excludes elements with data-no-click-sound (sliders, map drags, etc.)
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = (e.target as HTMLElement).closest(
        'button:not(:disabled):not([data-no-click-sound]), [role="button"]:not([data-no-click-sound])'
      )
      if (target) playFeedback()
    }
    document.addEventListener('click', handler, true)
    return () => document.removeEventListener('click', handler, true)
  }, [])

  return (
    <AppErrorBoundary>
      <AuthProvider>
        <HashRouter>
          <BackButtonHandler />
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* AGC: Admin panel — fullscreen, no MainLayout shell */}
              <Route path="/admin/*" element={<AdminRoute />} />
              {/* AGC: Invite acceptance — public route, no auth required */}
              <Route path="/invite" element={<InviteAcceptScreen />} />
              {/* AGC: Main app — currently MainMenu handles all screens */}
              {/* AGC: Phase 2 will add MainLayout + child routes here */}
              <Route path="/*" element={<AuthenticatedApp />} />
            </Routes>
          </Suspense>
        </HashRouter>
      </AuthProvider>
    </AppErrorBoundary>
  )
}

export default App
